/**
 * useStreamStore -- Global Zustand store for per-chat streaming state.
 *
 * This store holds the REACTIVE (render-relevant) projection of each chat's
 * streaming lifecycle. The actual EventSource connections and mutable working
 * copies live in the companion streamManager.ts module (non-React, module-level).
 *
 * The store is written ONLY by the streamManager -- components subscribe via
 * the exported selector hooks (useChatStreamSlice, useStreamingChatIds,
 * useBusyProjectIds).
 *
 * Design constraint: no non-serializable values (AbortController, intervals)
 * live here. Those belong in the manager's module-level Map.
 */

import { create } from 'zustand';
import type { Message } from '@/types/chat.types';
import type { RelayStatus, ComplianceInfo } from '@/hooks/code/useProjectChat';
// --- FEAT-053 TASK-045 (erro de envio) ---
import type { SendErrorInfo } from '@/components/chat/SendErrorBanner';
// --- fim FEAT-053 TASK-045 (erro de envio) ---

// ---------------------------------------------------------------------------
// Slice shape (per chat)
// ---------------------------------------------------------------------------

export type StreamStatus =
  | 'idle'
  | 'connecting'
  | 'streaming'
  | 'reconnecting'
  | 'terminal'
  | 'error';

export interface ChatStreamSlice {
  chatId: string;
  projectId: string;

  // lifecycle
  status: StreamStatus;
  isStreaming: boolean;
  isSending: boolean;
  hasPendingConnect: boolean;

  // the in-flight assistant turn
  streamingMessage: Message | null;
  currentTurnMessageId: string | null;

  // CONTINUOUS timer source of truth -- set ONCE per turn, NEVER reset on mount
  startedAt: number | null;

  // health / recovery
  relayStatus: RelayStatus;
  error: string | null;
  reconnectAttempts: number;

  // --- FEAT-053 TASK-045 (erro de envio) ---
  /** Turn ended by the server (SSE `error`) before the reply started; rendered as a legible banner. NOT `error`. */
  turnError: SendErrorInfo | null;
  // --- fim FEAT-053 TASK-045 (erro de envio) ---

  // compliance accumulated across the session
  complianceEvents: Map<string, ComplianceInfo>;
}

// ---------------------------------------------------------------------------
// Store state
// ---------------------------------------------------------------------------

interface StreamStoreState {
  slicesByChat: Record<string, ChatStreamSlice>;

  // Internal setters (used by streamManager only)
  _setSlice: (chatId: string, partial: Partial<ChatStreamSlice>) => void;
  _ensureSlice: (chatId: string, projectId: string) => void;
  _removeSlice: (chatId: string) => void;
}

function createDefaultSlice(chatId: string, projectId: string): ChatStreamSlice {
  return {
    chatId,
    projectId,
    status: 'idle',
    isStreaming: false,
    isSending: false,
    hasPendingConnect: false,
    streamingMessage: null,
    currentTurnMessageId: null,
    startedAt: null,
    relayStatus: 'online',
    error: null,
    reconnectAttempts: 0,
    turnError: null, // FEAT-053 TASK-045
    complianceEvents: new Map(),
  };
}

export const useStreamStore = create<StreamStoreState>((set) => ({
  slicesByChat: {},

  _setSlice: (chatId, partial) =>
    set((state) => {
      const existing = state.slicesByChat[chatId];
      if (!existing) return state;
      const updated = { ...existing, ...partial };
      return { slicesByChat: { ...state.slicesByChat, [chatId]: updated } };
    }),

  _ensureSlice: (chatId, projectId) =>
    set((state) => {
      if (state.slicesByChat[chatId]) return state;
      return {
        slicesByChat: {
          ...state.slicesByChat,
          [chatId]: createDefaultSlice(chatId, projectId),
        },
      };
    }),

  _removeSlice: (chatId) =>
    set((state) => {
      if (!state.slicesByChat[chatId]) return state;
      const next = { ...state.slicesByChat };
      delete next[chatId];
      return { slicesByChat: next };
    }),
}));

// ---------------------------------------------------------------------------
// Selector hooks (for components)
// ---------------------------------------------------------------------------

const EMPTY_SLICE: ChatStreamSlice = createDefaultSlice('', '');

/** Subscribe to a single chat's streaming slice. Returns a stable empty default when absent. */
export function useChatStreamSlice(chatId: string | null): ChatStreamSlice {
  return useStreamStore((s) =>
    chatId ? (s.slicesByChat[chatId] ?? EMPTY_SLICE) : EMPTY_SLICE,
  );
}

/** Set of chatIds currently streaming or connecting/reconnecting. */
export function useStreamingChatIds(): Set<string> {
  return useStreamStore((s) => {
    const ids = new Set<string>();
    for (const [id, slice] of Object.entries(s.slicesByChat)) {
      if (slice.isStreaming || slice.status === 'connecting' || slice.status === 'reconnecting') {
        ids.add(id);
      }
    }
    return ids;
  });
}

/** Set of projectIds that have an active stream (for sidebar busy bullet). */
export function useBusyProjectIds(): Set<string> {
  return useStreamStore((s) => {
    const ids = new Set<string>();
    for (const slice of Object.values(s.slicesByChat)) {
      if (slice.isStreaming || slice.isSending || slice.status === 'connecting' || slice.status === 'reconnecting') {
        ids.add(slice.projectId);
      }
    }
    return ids;
  });
}
